/**
 * useStockFinancials — fetches financial statements for the Financials tab.
 * Lazy: only fetches when ticker is non-empty (triggered by tab activation).
 */
import { useState, useEffect, useRef } from 'react'
import { getStockFinancials } from '@/lib/api'
import type { StockFinancials } from '@/lib/types'

interface StockFinancialsState {
  data: StockFinancials | null
  loading: boolean
  error: string | null
}

export function useStockFinancials(ticker: string): StockFinancialsState {
  const [data, setData] = useState<StockFinancials | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // Cache per ticker so we don't re-fetch when switching back to tab
  const cacheRef = useRef<Record<string, StockFinancials>>({})

  useEffect(() => {
    if (!ticker) return

    // Return cached data immediately
    if (cacheRef.current[ticker]) {
      setData(cacheRef.current[ticker])
      setError(null)
      return
    }

    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setLoading(true)
    setError(null)

    getStockFinancials(ticker)
      .then(d => {
        if (!ctrl.signal.aborted) {
          cacheRef.current[ticker] = d
          setData(d)
          setLoading(false)
        }
      })
      .catch(err => {
        if (!ctrl.signal.aborted) {
          setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load financial statements')
          setLoading(false)
        }
      })

    return () => ctrl.abort()
  }, [ticker])

  return { data, loading, error }
}
